/**
 * Project store for managing project-level state and loading
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { projectsApi } from '../lib/api';
import { logger } from '../lib/logger';
import type { Coordinate } from '../types';

const log = logger.child('ProjectStore');

export type LoadingState = 'idle' | 'loading' | 'loaded' | 'error';

interface RecentProject {
  name: string;
  date: string;
  photoCount: number;
  lastOpened: string;
}

interface ProjectState {
  // Project metadata
  currentProjectId: string | null;
  projectName: string | null;
  surveyDate: string | null;
  epsg: number;
  
  // Loading state
  loadingState: LoadingState;
  loadingProgress: number;
  loadingMessage: string;
  loadingError: string | null;
  
  // Recent projects
  recentProjects: RecentProject[];
  
  // Actions
  loadFromZip: (file: File) => Promise<void>;
  setLoadingState: (state: LoadingState) => void;
  setLoadingProgress: (progress: number, message: string) => void;
  reset: () => void;
  addRecentProject: (project: RecentProject) => void;
  removeRecentProject: (name: string) => void;
  
  // Selectors
  isLoaded: () => boolean;
}

export const useProjectStore = create<ProjectState>()(
  persist(
    (set, get) => ({
      // Initial state
      currentProjectId: null,
      projectName: null,
      surveyDate: null,
      epsg: 32632,
      loadingState: 'idle',
      loadingProgress: 0,
      loadingMessage: '',
      loadingError: null,
      recentProjects: [],

      loadFromZip: async (file: File) => {
        set({ 
          loadingState: 'loading', 
          loadingProgress: 0,
          loadingMessage: 'Uploading ZIP to server...',
          loadingError: null
        });
        
        try {
          // Upload ZIP to server
          const response = await projectsApi.importZip(file, (progressEvent) => {
            if (progressEvent.total) {
              const uploadProgress = Math.floor((progressEvent.loaded / progressEvent.total) * 100);
              set({ 
                loadingProgress: uploadProgress, 
                loadingMessage: `Uploading ZIP: ${uploadProgress}%...` 
              });
            }
          });

          const result = response.data.data;
          const projectId = result.projectId;
          const projectName = file.name.replace('.zip', '');
          
          log.info('Project imported successfully', {
            projectId,
            photoCount: result.photoCount,
            elementCount: result.elementCount,
          });
          
          if (result.errors && result.errors.length > 0) {
            log.warn('Import warnings', result.errors);
          }
          
          // Add to recent projects
          const recentProject: RecentProject = {
            name: projectName,
            date: new Date().toISOString(),
            photoCount: result.photoCount,
            lastOpened: new Date().toISOString()
          };
          get().addRecentProject(recentProject);
          
          set({
            currentProjectId: projectId,
            loadingState: 'loaded',
            loadingProgress: 100,
            loadingMessage: 'Project imported successfully!',
            projectName,
            surveyDate: null,
            epsg: 32632,
          });
          
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          log.error('Import failed', errorMessage);
          set({
            loadingState: 'error',
            loadingError: errorMessage,
            loadingMessage: `Error: ${errorMessage}`
          });
          throw error;
        }
      },

      setLoadingState: (state) => set({ loadingState: state }),
      
      setLoadingProgress: (progress, message) => set({ 
        loadingProgress: progress, 
        loadingMessage: message 
      }),

      reset: () => {
        set({
          currentProjectId: null,
          projectName: null,
          surveyDate: null,
          epsg: 32632,
          loadingState: 'idle',
          loadingProgress: 0,
          loadingMessage: '',
          loadingError: null,
        });
      },

      addRecentProject: (project) => {
        set((state) => {
          // Remove if already exists
          const filtered = state.recentProjects.filter(p => p.name !== project.name);
          // Add to beginning
          const updated = [project, ...filtered].slice(0, 10); // Keep max 10
          return { recentProjects: updated };
        });
      },

      removeRecentProject: (name) => {
        set((state) => ({
          recentProjects: state.recentProjects.filter(p => p.name !== name)
        }));
      },

      isLoaded: () => get().loadingState === 'loaded',
    }),
    {
      name: 'elifly-project',
      // Only persist certain fields
      partialize: (state) => ({
        recentProjects: state.recentProjects,
        // Don't persist photos or loading state
      }),
    }
  )
);
